import { useCallback, useEffect, useState } from "react";
import { ArrowRight, Check, CreditCard, RefreshCw, ShieldCheck } from "lucide-react";
import { useSession } from "./lib/session";

type PlanId = "free" | "plus" | "professional" | "team";
type BillingInterval = "monthly" | "annual";
type SubscriptionStatus = "free" | "incomplete" | "trialing" | "active" | "past_due" | "paused" | "cancelled" | "unpaid";

interface Plan {
  id: PlanId;
  name: string;
  monthlyAmountCents: number;
  annualAmountCents: number;
  includedUsers: number;
  completedDocumentsPerMonth: number;
  activeTemplates: number | "unlimited";
  archivedDocumentsMaximum: number;
  advancedWorkflows: boolean;
  aiTranslation: "none" | "limited" | "included";
  ai?: { monthlyCredits: number; enabledModes: readonly ("proofread" | "summarize" | "translate")[]; privacy: "standard" | "no_collection" | "zero_retention"; maxDocumentCharacters: number };
}

interface Subscription {
  planId: PlanId;
  billingInterval: BillingInterval;
  status: SubscriptionStatus;
  stripeCustomerId?: string;
  updatedAt: string;
}

interface Checkout { checkoutUrl: string }
interface Portal { portalUrl: string }

const selectedPlanKey = "bluedottedline:selected-plan";
const pendingCheckoutKey = "bluedottedline:pending-checkout";

async function request<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { authorization: `Bearer ${token}`, ...(init?.body ? { "content-type": "application/json" } : {}), ...init?.headers },
  });
  const payload = await response.json() as { data?: T; error?: { message?: string }; errors?: readonly { message?: string }[] };
  if (!response.ok || payload.data === undefined) throw new Error(payload.errors?.[0]?.message ?? payload.error?.message ?? "Billing could not be completed.");
  return payload.data;
}

function money(cents: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
}

function planFeatures(plan: Plan): readonly string[] {
  return [
    `${plan.completedDocumentsPerMonth} completed documents / month`,
    `Up to ${plan.archivedDocumentsMaximum.toLocaleString()} archived documents`,
    `${plan.activeTemplates === "unlimited" ? "Unlimited" : plan.activeTemplates} active template${plan.activeTemplates === 1 ? "" : "s"}`,
    `${plan.includedUsers} included user${plan.includedUsers === 1 ? "" : "s"}`,
    plan.advancedWorkflows ? "Sequential and parallel signing" : "Sequential signing workflow",
    plan.id === "free" ? "Text, date and signature fields" : plan.id === "plus" ? "Adds checkbox and radio fields" : "All field types included",
    plan.id === "professional" || plan.id === "team" ? "SES and optional passkey-confirmed AdES" : "Simple electronic signatures (SES)",
    "Product guidance included",
    ...(plan.ai ? [`${plan.ai.monthlyCredits.toLocaleString()} document AI credits / month`, `Document AI: ${plan.ai.enabledModes.join(", ") || "not included"}`, plan.ai.privacy === "zero_retention" ? "Enhanced document privacy" : plan.ai.privacy === "no_collection" ? "Private document AI" : "Standard document privacy"] : []),
  ];
}

export function BillingPage() {
  const { token = "" } = useSession();
  const [plans, setPlans] = useState<readonly Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription>();
  const [interval, setInterval] = useState<BillingInterval>("monthly");
  const [selectedPlan, setSelectedPlan] = useState<PlanId | undefined>(() => {
    try { return (sessionStorage.getItem(selectedPlanKey) as PlanId | null) ?? undefined; } catch { return undefined; }
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string>();
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  const load = useCallback(async () => {
    const [availablePlans, current] = await Promise.all([
      request<readonly Plan[]>(token, "/api/v1/billing/plans"),
      request<Subscription>(token, "/api/v1/billing/subscription"),
    ]);
    setPlans(availablePlans);
    setSubscription(current);
    setInterval(current.billingInterval);
    if (["active", "trialing"].includes(current.status)) {
      try { sessionStorage.removeItem(pendingCheckoutKey); sessionStorage.removeItem(selectedPlanKey); } catch { /* Storage can be unavailable in privacy mode. */ }
      setSelectedPlan(undefined);
      setMessage("Your membership is active and up to date.");
    }
  }, [token]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void load().catch((cause) => active && setError(cause instanceof Error ? cause.message : "Billing is unavailable.")).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [load]);

  useEffect(() => {
    let pending = false;
    try { pending = sessionStorage.getItem(pendingCheckoutKey) === "true"; } catch { /* Storage can be unavailable in privacy mode. */ }
    if (!pending || subscription?.status === "active" || subscription?.status === "trialing") return;
    setMessage("Confirming your membership change. This page will refresh automatically.");
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      void load().catch(() => undefined);
      if (attempts >= 15) window.clearInterval(timer);
    }, 2_000);
    return () => window.clearInterval(timer);
  }, [load, subscription?.status]);

  const startCheckout = async (planId: Exclude<PlanId, "free">) => {
    setBusy(planId); setError(undefined); setMessage("Opening secure checkout…");
    try {
      const checkout = await request<Checkout>(token, "/api/v1/billing/subscription/checkout", {
        method: "POST",
        body: JSON.stringify({ planId, billingInterval: interval, idempotencyKey: crypto.randomUUID() }),
      });
      try { sessionStorage.setItem(selectedPlanKey, planId); sessionStorage.setItem(pendingCheckoutKey, "true"); } catch { /* Storage can be unavailable in privacy mode. */ }
      window.location.assign(checkout.checkoutUrl);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Secure checkout could not be opened.");
      setBusy(undefined);
    }
  };

  const openPortal = async (targetPlanId?: PlanId) => {
    setBusy(targetPlanId ?? "portal"); setError(undefined);
    try {
      const portal = await request<Portal>(token, "/api/v1/billing/portal", { method: "POST", body: JSON.stringify({ ...(targetPlanId ? { targetPlanId, billingInterval: interval } : {}) }) });
      window.location.assign(portal.portalUrl);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Membership management could not be opened.");
      setBusy(undefined);
    }
  };

  if (loading) return <main className="billing-state"><span className="spinner" /><h1>Loading billing</h1></main>;

  return <div className="page billing-page">
    <section className="billing-hero reveal-block">
      <div><p className="eyebrow">Membership and payments</p><h1>Manage your BlueDottedLine membership.</h1><p>Compare memberships, change your level securely, or update your payment method. Before downgrading, your archived-document count must fit the new membership.</p></div>
      <article className="billing-current-card"><span>Current membership</span><strong>{plans.find((plan) => plan.id === subscription?.planId)?.name ?? "Free"}</strong><i className={`billing-status billing-status-${subscription?.status ?? "free"}`}>{subscription?.status?.replace("_", " ") ?? "free"}</i>{subscription?.stripeCustomerId && <button className="button button-light" type="button" disabled={busy === "portal"} onClick={() => void openPortal()}><CreditCard /> {busy === "portal" ? "Opening…" : "Update payment method"}</button>}</article>
    </section>

    <section className="billing-controls" aria-label="Billing frequency"><span>Billing frequency</span><div><button type="button" className={interval === "monthly" ? "active" : ""} onClick={() => setInterval("monthly")}>Monthly</button><button type="button" className={interval === "annual" ? "active" : ""} onClick={() => setInterval("annual")}>Annual <small>Save up to 20%</small></button></div></section>
    {message && <p className="billing-message" role="status"><ShieldCheck /> {message}</p>}
    {error && <p className="billing-error" role="alert">{error} <button type="button" onClick={() => void load()}><RefreshCw /> Retry</button></p>}

    <section className="billing-plan-grid" aria-label="Subscription plans">
      {plans.map((plan) => {
        const current = subscription?.planId === plan.id && ["active", "trialing", "free"].includes(subscription.status);
        const managedByStripe = Boolean(subscription?.stripeCustomerId);
        const highlighted = selectedPlan === plan.id || plan.id === "professional";
        const amount = interval === "monthly" ? plan.monthlyAmountCents : plan.annualAmountCents;
        return <article key={plan.id} className={`${highlighted ? "billing-plan-featured" : ""}${current ? " billing-plan-current" : ""}`}>
          {current && <span className="billing-plan-flag">CURRENT PLAN</span>}
          {!current && selectedPlan === plan.id && <span className="billing-plan-flag">YOUR SELECTION</span>}
          <h2>{plan.name}</h2><div className="billing-price"><strong>{money(amount)}</strong><span>{plan.id === "free" ? "forever" : interval === "monthly" ? "per month" : "per year"}</span></div>
          <ul>{planFeatures(plan).map((feature) => <li key={feature}><Check /> {feature}</li>)}</ul>
          {current ? <button className="button button-quiet" type="button" disabled>Current membership</button> : plan.id === "free" ? <button className="button button-quiet" type="button" disabled={Boolean(busy) || !managedByStripe} onClick={() => void openPortal("free")}>{busy === "free" ? "Checking eligibility…" : "Downgrade to Free"} <ArrowRight /></button> : <button className="button button-dark" type="button" disabled={Boolean(busy)} onClick={() => void (managedByStripe ? openPortal(plan.id) : startCheckout(plan.id as Exclude<PlanId, "free">))}>{busy === plan.id ? "Checking eligibility…" : managedByStripe ? "Change membership" : `Choose ${plan.name}`} <ArrowRight /></button>}
        </article>;
      })}
    </section>
    <p className="billing-tax-note">Membership changes and cancellations are applied after payment confirmation. Prices exclude applicable taxes.</p>
  </div>;
}
